import { useState } from 'react';
import OfficeLayout from '../../components/OfficeLayout';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';
import { useAuth } from '../../contexts/AuthContext';
import { getUsers, formatApiError } from '../../utils/api';
import api from '../../utils/api';
import { Plus, Trash2, User, Shield, Briefcase, Truck } from 'lucide-react';

const roleOptions = [
  { value: 'admin', label: 'Admin', icon: Shield, color: 'purple' },
  { value: 'office_staff', label: 'Office Staff', icon: Briefcase, color: 'blue' },
  { value: 'driver', label: 'Driver', icon: Truck, color: 'amber' },
];

const roleBadge = (role) => {
  const cfg = { admin: 'bg-purple-100 text-purple-700', office_staff: 'bg-blue-100 text-blue-700', driver: 'bg-amber-100 text-amber-700' };
  const labels = { admin: 'Admin', office_staff: 'Office Staff', driver: 'Driver' };
  return <span className={`text-xs font-semibold px-2 py-0.5 rounded ${cfg[role] || 'bg-gray-100 text-gray-700'}`}>{labels[role] || role}</span>;
};

export default function UserProfiles() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [role, setRole] = useState('office_staff');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const { user: currentUser } = useAuth();

  const loadUsers = async () => {
    try {
      const { data } = await getUsers();
      setUsers(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const { refresh } = useAutoRefresh(loadUsers, 30000);

  const handleCreate = async (e) => {
    e.preventDefault();
    setError(''); setMsg(''); setCreating(true);
    try {
      await api.post('/users/create-simple', { name: name.trim(), role });
      setMsg(`User "${name.trim()}" created as ${role.replace('_', ' ')}!`);
      setShowForm(false);
      setName('');
      setRole('office_staff');
      refresh(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (userId, userName) => {
    if (!window.confirm(`Delete user "${userName}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/users/${userId}`);
      setMsg(`User "${userName}" deleted`);
      refresh(true);
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  if (currentUser?.role !== 'admin') {
    return (
      <OfficeLayout>
        <div className="text-center py-16 text-[#6B7280]">
          <Shield className="mx-auto mb-3 text-[#9CA3AF]" size={40} />
          <p>Admin access required to manage user profiles.</p>
        </div>
      </OfficeLayout>
    );
  }

  return (
    <OfficeLayout>
      <div data-testid="user-profiles-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-[#1A1A2E]">User Profiles</h2>
            <p className="text-sm text-[#6B7280] mt-1">Manage users and their roles</p>
          </div>
          <button onClick={() => { setShowForm(!showForm); setError(''); setMsg(''); }}
            className="flex items-center gap-2 bg-[#0D9488] hover:bg-[#0F766E] text-white px-5 py-2.5 rounded-lg font-medium text-sm"
            data-testid="add-user-button">
            <Plus size={16} /> Add User
          </button>
        </div>

        {msg && <div className="mb-4 px-4 py-3 rounded-lg bg-green-50 border border-green-200 text-green-700 text-sm">{msg}</div>}
        {error && <div className="mb-4 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}

        {showForm && (
          <div className="bg-white border border-gray-200 rounded-lg p-5 mb-4">
            <h3 className="text-sm font-semibold text-[#1A1A2E] mb-4">Create New User</h3>
            <form onSubmit={handleCreate} className="flex flex-wrap gap-3 items-end">
              <div className="flex-1 min-w-[200px]">
                <label className="block text-sm font-medium text-[#4B5563] mb-1.5">Name *</label>
                <input value={name} onChange={e => setName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0D9488]"
                  required placeholder="Full name" data-testid="new-user-name" />
              </div>
              <div className="min-w-[180px]">
                <label className="block text-sm font-medium text-[#4B5563] mb-1.5">Role *</label>
                <div className="flex gap-2">
                  {roleOptions.map(opt => (
                    <button key={opt.value} type="button" onClick={() => setRole(opt.value)}
                      className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                        role === opt.value
                          ? 'border-[#0D9488] bg-teal-50 text-[#0D9488]'
                          : 'border-gray-200 text-[#6B7280] hover:border-gray-300'
                      }`}
                      data-testid={`role-${opt.value}`}>
                      <opt.icon size={14} /> {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              <button type="submit" disabled={creating}
                className="px-5 py-2 bg-[#0D9488] text-white rounded-lg text-sm font-medium disabled:opacity-50"
                data-testid="create-user-submit">
                {creating ? 'Creating...' : 'Create User'}
              </button>
            </form>
          </div>
        )}

        <div className="bg-white border border-gray-200 rounded-lg">
          {loading ? (
            <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-2 border-[#0D9488] border-t-transparent"></div></div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/50">
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-[#6B7280]">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-[#6B7280]">Role</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-[#6B7280]">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-[#6B7280]">Created</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-[#6B7280]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                    <td className="px-6 py-3.5 font-medium text-[#1A1A2E] flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-xs font-bold text-[#6B7280]">
                        {u.name?.charAt(0) || '?'}
                      </div>
                      {u.name}
                    </td>
                    <td className="px-6 py-3.5">{roleBadge(u.role)}</td>
                    <td className="px-6 py-3.5">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded ${u.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {u.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-3.5 text-[#6B7280]">{new Date(u.created_at).toLocaleDateString()}</td>
                    <td className="px-6 py-3.5">
                      {u.id !== currentUser?.id ? (
                        <button onClick={() => handleDelete(u.id, u.name)} className="text-red-500 hover:text-red-700" data-testid={`delete-user-${u.id}`}>
                          <Trash2 size={16} />
                        </button>
                      ) : (
                        <span className="text-xs text-[#9CA3AF]">You</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </OfficeLayout>
  );
}
